import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from "@/hooks/useToast";

// Types for Job Card Defect Log
export interface DefectLog {
  id: string;
  job_card_id: string;
  work_order_id: string;
  operation_id: string;
  defect_type: 'dimensional' | 'surface_finish' | 'material' | 'assembly' | 'functional' | 'other';
  defect_description: string;
  defect_severity: 'minor' | 'major' | 'critical';
  quantity_affected: number;
  root_cause?: string;
  corrective_action?: string;
  rework_required: boolean;
  rework_quantity?: number;
  rework_status?: 'pending' | 'in_progress' | 'completed' | 'rejected';
  inspector_id: string;
  inspector_name?: string;
  detected_at: string;
  resolved_at?: string;
  ncr_id?: string; // Link to Non-Conformance Report
  status: 'open' | 'under_review' | 'resolved' | 'closed';
  created_at: string;
  updated_at: string;
}

export interface DefectLogRequest {
  jobCardId: string;
  defectType: DefectLog['defect_type'];
  defectDescription: string;
  defectSeverity: DefectLog['defect_severity'];
  quantityAffected: number;
  rootCause?: string;
  correctiveAction?: string;
  reworkRequired: boolean;
  reworkQuantity?: number;
  inspectorId: string;
}

export interface ReworkUpdateRequest {
  defectLogId: string;
  reworkStatus: DefectLog['rework_status'];
  reworkQuantity?: number;
  remarks?: string;
  updatedBy: string;
}

type UUID = string;

/**
 * Hook for managing Job Card Defect Logs
 * Handles defect recording, rework management, NCR linking, and quality analytics
 */
export const useJobCardDefectLog = () => {
  const [loading, setLoading] = useState(false);
  const [defectLogs, setDefectLogs] = useState<DefectLog[]>([]);
  const { toast } = useToast();

  /**
   * Fetch defect logs for a job card
   */
  const fetchDefectLogs = useCallback(async (jobCardId: UUID): Promise<DefectLog[]> => {
    try {
      setLoading(true);
      
      // Get job card details
      const { data: jobCardData, error: jobCardError } = await supabase
        .from('job_cards')
        .select('work_order_id, work_order_operation_id')
        .eq('id', jobCardId)
        .single();
      
      if (jobCardError) throw jobCardError;
      
      // Fetch defect logs from the dedicated defect_logs table
      const { data, error } = await supabase
        .from('defect_logs')
        .select(`
          *,
          reported_by_user:reported_by(full_name),
          reviewed_by_user:reviewed_by(full_name),
          closed_by_user:closed_by(full_name)
        `)
        .eq('job_card_id', jobCardId)
        .order('reported_at', { ascending: false });
      
      if (error) throw error;
      
      // Map audit log entries to DefectLog format
      const logs: DefectLog[] = (data || []).map((item: any) => {
        const defectData = typeof item.new_value === 'string' 
          ? JSON.parse(item.new_value) 
          : item.new_value || {};
        
        return {
          id: item.id,
          job_card_id: jobCardId,
          work_order_id: jobCardData.work_order_id,
          operation_id: jobCardData.work_order_operation_id,
          defect_type: defectData.defect_type || 'other',
          defect_description: defectData.defect_description || 'Unknown defect',
          defect_severity: defectData.defect_severity || 'minor',
          quantity_affected: defectData.quantity_affected || 1,
          root_cause: defectData.root_cause,
          corrective_action: defectData.corrective_action,
          rework_required: defectData.rework_required || false,
          rework_quantity: defectData.rework_quantity,
          rework_status: defectData.rework_status || 'pending',
          inspector_id: item.user_id,
          inspector_name: item.user?.full_name,
          detected_at: item.timestamp,
          resolved_at: defectData.resolved_at,
          ncr_id: defectData.ncr_id,
          status: defectData.status || 'open',
          created_at: item.timestamp,
          updated_at: item.timestamp,
        };
      });
      
      setDefectLogs(logs);
      return logs;
    } catch (error: any) {
      toast({
        title: 'Error fetching defect logs',
        description: error.message,
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Record a new defect
   */
  const recordDefect = useCallback(async (request: DefectLogRequest): Promise<DefectLog | null> => {
    try {
      setLoading(true);
      
      // Get job card details
      const { data: jobCardData, error: jobCardError } = await supabase
        .from('job_cards')
        .select('work_order_id, work_order_operation_id')
        .eq('id', request.jobCardId)
        .single();
      
      if (jobCardError) throw jobCardError;
      
      const defectData = {
        defect_type: request.defectType,
        defect_description: request.defectDescription,
        defect_severity: request.defectSeverity,
        quantity_affected: request.quantityAffected,
        root_cause: request.rootCause,
        corrective_action: request.correctiveAction,
        rework_required: request.reworkRequired,
        rework_quantity: request.reworkQuantity,
        rework_status: request.reworkRequired ? 'pending' : undefined,
        status: 'open',
        work_order_id: jobCardData.work_order_id,
        operation_id: jobCardData.work_order_operation_id
      };
      
      // Store defect log in the dedicated defect_logs table
      const { data: logData, error: logError } = await supabase
        .from('defect_logs')
        .insert({
          job_card_id: request.jobCardId,
          work_order_id: jobCardData.work_order_id,
          work_order_operation_id: jobCardData.work_order_operation_id,
          defect_type: request.defectType,
          defect_description: request.defectDescription,
          defect_severity: request.defectSeverity,
          quantity_affected: request.quantityAffected,
          root_cause: request.rootCause,
          corrective_action: request.correctiveAction,
          rework_required: request.reworkRequired,
          rework_quantity: request.reworkQuantity,
          rework_status: request.reworkRequired ? 'pending' : null,
          status: 'open',
          reported_by: request.inspectorId,
          reported_at: new Date().toISOString()
        })
        .select('*')
        .single();
      
      if (logError) throw logError;
      
      // Create NCR if severity is major or critical
      if (request.defectSeverity === 'major' || request.defectSeverity === 'critical') {
        await createNCRForDefect(request.jobCardId, logData.id, defectData, request.inspectorId);
      }
      
      // Update job card quantities if rework is required
      if (request.reworkRequired && request.reworkQuantity) {
        await updateJobCardForRework(request.jobCardId, request.reworkQuantity);
      }
      
      // Create the defect log object to return
      const newDefectLog: DefectLog = {
        id: logData.id,
        job_card_id: request.jobCardId,
        work_order_id: jobCardData.work_order_id,
        operation_id: jobCardData.work_order_operation_id,
        defect_type: request.defectType,
        defect_description: request.defectDescription,
        defect_severity: request.defectSeverity,
        quantity_affected: request.quantityAffected,
        root_cause: request.rootCause,
        corrective_action: request.correctiveAction,
        rework_required: request.reworkRequired,
        rework_quantity: request.reworkQuantity,
        rework_status: request.reworkRequired ? 'pending' : undefined,
        inspector_id: request.inspectorId,
        inspector_name: undefined,
        detected_at: logData.timestamp,
        resolved_at: undefined,
        ncr_id: undefined,
        status: 'open',
        created_at: logData.timestamp,
        updated_at: logData.timestamp,
      };
      
      // Refresh defect logs
      await fetchDefectLogs(request.jobCardId);
      
      toast({
        title: 'Defect Recorded',
        description: `${request.defectSeverity} defect logged for ${request.quantityAffected} units`,
        variant: request.defectSeverity === 'critical' ? 'destructive' : 'default',
      });
      
      return newDefectLog;
    } catch (error: any) {
      toast({
        title: 'Error recording defect',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchDefectLogs, toast]);

  /**
   * Update rework status
   */
  const updateReworkStatus = useCallback(async (request: ReworkUpdateRequest): Promise<boolean> => {
    try {
      setLoading(true);
      
      // Get current defect log data
      const { data: currentLog, error: fetchError } = await supabase
        .from('defect_logs')
        .select('*')
        .eq('id', request.defectLogId)
        .single();
      
      if (fetchError) throw fetchError;
      
      const currentData = typeof currentLog.new_value === 'string' 
        ? JSON.parse(currentLog.new_value) 
        : currentLog.new_value || {};
      
      const updatedData = {
        ...currentData,
        rework_status: request.reworkStatus,
        rework_quantity: request.reworkQuantity || currentData.rework_quantity,
        status: request.reworkStatus === 'completed' ? 'resolved' : currentData.status,
        resolved_at: request.reworkStatus === 'completed' ? new Date().toISOString() : currentData.resolved_at
      };
      
      // Update the job card activity entry
      const { error: updateError } = await supabase
        .from('job_card_activities')
        .update({
          metadata: JSON.stringify(updatedData)
        })
        .eq('id', request.defectLogId);
      
      if (updateError) throw updateError;
      
      // Create additional job card activity entry for rework update
      await supabase
        .from('job_card_activities')
        .insert({
          job_card_id: currentLog.job_card_id,
          activity_type: 'rework_updated',
          description: `Rework status updated from ${currentData.rework_status} to ${request.reworkStatus}`,
          user_id: request.updatedBy,
          metadata: {
            table_name: 'defect_logs',
            record_id: currentLog.record_id,
            action: 'rework_updated',
            field_name: 'rework_status',
            old_value: currentData.rework_status,
            new_value: request.reworkStatus,
            category: 'defect',
            severity: 'info'
          },
          timestamp: new Date().toISOString()
        });
      
      toast({
        title: 'Rework Updated',
        description: `Rework status changed to ${request.reworkStatus}`,
        variant: 'default',
      });
      
      return true;
    } catch (error: any) {
      toast({
        title: 'Error updating rework status',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Close defect log
   */
  const closeDefectLog = useCallback(async (defectLogId: UUID, resolution: string, closedBy: UUID): Promise<boolean> => {
    try {
      setLoading(true);
      
      // Get current defect log data
      const { data: currentLog, error: fetchError } = await supabase
        .from('job_card_activities')
        .select('*')
        .eq('id', defectLogId)
        .single();
      
      if (fetchError) throw fetchError;
      
      const currentData = typeof currentLog.new_value === 'string' 
        ? JSON.parse(currentLog.new_value) 
        : currentLog.new_value || {};
      
      const updatedData = {
        ...currentData,
        status: 'closed',
        resolution,
        resolved_at: new Date().toISOString(),
        closed_by: closedBy
      };
      
      // Update the job card activity entry
      const { error: updateError } = await supabase
        .from('job_card_activities')
        .update({
          metadata: JSON.stringify(updatedData)
        })
        .eq('id', defectLogId);
      
      if (updateError) throw updateError;
      
      toast({
        title: 'Defect Closed',
        description: 'Defect log has been successfully closed',
        variant: 'default',
      });
      
      return true;
    } catch (error: any) {
      toast({
        title: 'Error closing defect log',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Get defect analytics for a job card
   */
  const getDefectAnalytics = useCallback((logs: DefectLog[]) => {
    const analytics = {
      total_defects: logs.length,
      by_severity: {} as Record<string, number>,
      by_type: {} as Record<string, number>,
      by_status: {} as Record<string, number>,
      rework_required: 0,
      rework_completed: 0,
      total_affected_quantity: 0,
      ncr_count: 0,
      open_defects: 0
    };
    
    logs.forEach(log => {
      // Count by severity
      analytics.by_severity[log.defect_severity] = 
        (analytics.by_severity[log.defect_severity] || 0) + 1;
      
      // Count by type
      analytics.by_type[log.defect_type] = 
        (analytics.by_type[log.defect_type] || 0) + 1;
      
      // Count by status
      analytics.by_status[log.status] = 
        (analytics.by_status[log.status] || 0) + 1;
      
      // Count rework
      if (log.rework_required) {
        analytics.rework_required++;
        if (log.rework_status === 'completed') {
          analytics.rework_completed++;
        }
      }
      
      // Sum affected quantities
      analytics.total_affected_quantity += log.quantity_affected;
      
      // Count NCRs
      if (log.ncr_id) {
        analytics.ncr_count++;
      }
      
      // Count open defects
      if (log.status === 'open') {
        analytics.open_defects++;
      }
    });
    
    return analytics;
  }, []);

  return {
    loading,
    defectLogs,
    fetchDefectLogs,
    recordDefect,
    updateReworkStatus,
    closeDefectLog,
    getDefectAnalytics
  };
};

/**
 * Helper function to create NCR for major/critical defects
 */
async function createNCRForDefect(jobCardId: UUID, defectLogId: UUID, defectData: any, inspectorId: UUID) {
  try {
    await supabase
      .from('job_card_activities')
      .insert({
        job_card_id: jobCardId,
        activity_type: 'ncr_created',
        description: `NCR created for critical defect`,
        user_id: inspectorId,
        metadata: {
          table_name: 'ncr_created',
          record_id: defectLogId,
          action: 'insert',
          new_value: {
            defect_log_id: defectLogId,
            defect_description: defectData.defect_description,
            defect_severity: defectData.defect_severity,
            auto_created: true
          },
          category: 'defect',
          severity: 'critical'
        },
        timestamp: new Date().toISOString()
      });
  } catch (error) {
    console.error('Error creating NCR for defect:', error);
  }
}

/**
 * Helper function to update job card for rework quantities
 */
async function updateJobCardForRework(jobCardId: UUID, reworkQuantity: number) {
  try {
    await supabase
      .from('job_card_activities')
      .insert({
        job_card_id: jobCardId,
        activity_type: 'rework_quantity_added',
        description: `Rework quantity added: ${reworkQuantity}`,
        user_id: (await supabase.auth.getUser()).data.user?.id || 'current-user',
        metadata: {
          table_name: 'job_cards',
          record_id: jobCardId,
          action: 'rework_quantity_added',
          field_name: 'rework_quantity',
          old_value: null,
          new_value: reworkQuantity,
          category: 'defect',
          severity: 'info'
        },
        timestamp: new Date().toISOString()
      });
  } catch (error) {
    console.error('Error updating job card for rework:', error);
  }
}
